import { z } from './zod';

/** UUIDv7 primary key (docs/01-architectuur/04-datamodel.md). */
export const Id = z.uuid().openapi({ example: '01926f3a-8a1b-7c2d-9e3f-0a1b2c3d4e5f' });
export type Id = z.infer<typeof Id>;

/** Point in time, ISO 8601 in UTC. */
export const IsoDateTime = z.iso.datetime({ offset: true }).openapi({ example: '2026-09-25T08:00:00Z' });

/** Calendar date without time, a local date in Europe/Amsterdam. */
export const IsoDate = z.iso.date().openapi({ example: '2026-09-25' });

/** ISO 4217 currency code. */
export const Currency = z
  .string()
  .length(3)
  .regex(/^[A-Z]{3}$/)
  .openapi({ example: 'EUR' });

/** Amount in cents plus currency. Never a float. */
export const Money = z.object({ amountCents: z.number().int(), currency: Currency }).openapi('Money');

/** Version for ETag / optimistic concurrency. */
export const Version = z.number().int().positive();

/** Standard list query: cursor pagination (no offset), limit 1..200, default 50. */
export const ListQuery = z.object({
  limit: z.coerce.number().int().min(1).max(200).optional().describe('Page size, default 50, max 200'),
  cursor: z.string().max(500).optional().describe('Opaque cursor from nextCursor of the previous page'),
});

/** A page of items: { items, nextCursor }. */
export function Paginated<T extends z.ZodTypeAny>(item: T) {
  return z.object({ items: z.array(item), nextCursor: z.string().nullable() });
}

/**
 * One change in the history of a record (CO-0551): from the audit trail, with who made it (a person, an agent or the
 * system) and the fields that changed as [before, after].
 */
export const HistoryEntry = z
  .object({
    id: Id,
    at: IsoDateTime,
    action: z.string().describe('The audit action, such as task.updated or comment.created'),
    actorId: Id.nullable(),
    actorName: z.string().nullable(),
    actorKind: z.enum(['human', 'agent', 'system']),
    agentRunId: Id.nullable().describe('The agent run that made the change (CO-0969)'),
    changes: z.record(z.string(), z.unknown()).nullable().describe('Per field [before, after]'),
  })
  .openapi('HistoryEntry');

/**
 * A list filter that accepts both a comma separated query parameter (REST: ?status=draft,confirmed)
 * and a JSON array (MCP tools).
 */
export function CsvEnum<const T extends readonly [string, ...string[]]>(values: T) {
  return z.preprocess(
    (v) => (typeof v === 'string' ? v.split(',').filter(Boolean) : v),
    z.array(z.enum(values)).describe(`One or more of: ${values.join(', ')}`),
  );
}

/** A boolean filter that accepts "true"/"false" (query string) and true/false (JSON). */
export const QueryBool = z.preprocess(
  (v) => (v === 'true' ? true : v === 'false' ? false : v),
  z.boolean(),
);

/** An integer that accepts a query string or a JSON number. */
export const QueryInt = z.coerce.number().int();

/**
 * The version a delete (or get) action checks: those actions take their input from the query string, where
 * ?expectedVersion=12 arrives as "12" (#1480). Body actions keep a plain z.number().
 */
export const QueryVersion = QueryInt.positive().optional().describe('Version you read; the update fails if it changed');

/** RFC 9457 problem details, as returned for every error. */
export const Problem = z
  .object({
    type: z.string(),
    title: z.string(),
    status: z.number().int(),
    detail: z.string().optional(),
    instance: z.string().optional(),
    code: z.string(),
    requestId: z.string().optional(),
    errors: z.array(z.object({ pointer: z.string(), message: z.string() })).optional(),
    approvalId: z.string().optional().describe('With approval-pending: the request to follow with approval.request.get'),
  })
  .openapi('Problem');
export type Problem = z.infer<typeof Problem>;

/**
 * The VAT codes of an invoice or proposal line (CO-0362, CO-0365): standard 21%, reduced 9%, zero, reverse charged,
 * outside the EU (not subject to Dutch VAT, #108), exempt. Shared because billing and proposals both price lines;
 * billing holds the rates and the rules.
 */
export const VAT_CODES = ['standard', 'reduced', 'zero', 'reverse_charge', 'outside_eu', 'exempt'] as const;

/**
 * The fields of a create schema as the input of its update action (#109): each field optional and without its
 * default, so an omitted field stays unchanged instead of falling back to the default of a new record.
 */
export function updateFields<T extends Record<string, z.ZodType>>(fields: T): { [K in keyof T]: z.ZodOptional<T[K]> } {
  return Object.fromEntries(Object.entries(fields).map(([k, v]) => [k, (v instanceof z.ZodDefault ? (v.unwrap() as z.ZodType) : v).optional()])) as {
    [K in keyof T]: z.ZodOptional<T[K]>;
  };
}

/**
 * A link of a client or project to where its work lives (CO-0487): repositories, the SharePoint folder, the memory
 * project, a pinned issue view. Only http and https, so a link never runs script. At most 20 per record.
 */
export const LINK_KINDS = ['repo', 'sharepoint', 'memory', 'issues', 'other'] as const;
export const RecordLink = z
  .object({
    kind: z.enum(LINK_KINDS),
    label: z.string().trim().min(1).max(100),
    url: z.url({ protocol: /^https?$/ }).max(2000),
  })
  .openapi('RecordLink');
export type RecordLink = z.infer<typeof RecordLink>;
export const RecordLinks = z.array(RecordLink).max(20);

// ---------------------------------------------------------------- uploads without base64 (CO-1362)
// Shared by files (attachments) and proposals (the PDF): an upload starts on the record it is for.

/** What an upload may be: pdf, docx, xlsx, pptx, png, jpg, txt, md, csv and zip. */
export const UPLOAD_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'image/png',
  'image/jpeg',
  'text/plain',
  'text/markdown',
  'text/csv',
  'application/zip',
] as const;

/** What the client announces when it starts an upload; the PUT must match it. */
export const UploadSpec = z
  .object({
    fileName: z.string().trim().min(1).max(200),
    contentType: z.string().trim().min(1).max(200).describe('One of pdf, docx, xlsx, pptx, png, jpg, txt, md, csv or zip'),
    sizeBytes: z.number().int().min(1).max(25 * 1024 * 1024).describe('At most 25 MB'),
    sha256: z.string().regex(/^[0-9a-fA-F]{64}$/).describe('Hex SHA-256 of the bytes; the PUT is refused when it differs'),
  })
  .strict();

export const UploadTicket = z
  .object({
    uploadId: Id,
    uploadUrl: z.string().describe('PUT the raw bytes here, without an Authorization header: the URL is the credential, single use'),
    method: z.literal('PUT'),
    expiresAt: IsoDateTime.describe('15 minutes after the start'),
    maxBytes: z.number().int(),
    hint: z.string(),
  })
  .openapi('UploadTicket');
